import { useEffect, useRef, useState } from 'react';
import { Path, UseFormReturn } from 'react-hook-form';
import { useAppDispatch } from '../../store/hook';
import { openAlert } from '../../store/features/modalSlice';
import { StudentProfileForm } from '../../constants/types/student-profile-form';

type SectionForm = Pick<
  UseFormReturn<StudentProfileForm>,
  'getValues' | 'setValue' | 'trigger' | 'watch' | 'reset'
>;

/**
 * 영역(카드)별로 펼쳐서 수정→검증→저장하는 화면(학생 내 정보, LMS 학생 상세)의
 * 공통 상태 머신. 변경 감지·저장 API 호출은 화면마다 달라 `save`로 위임받는다.
 */
export function useSectionEditForm<K extends string>({
  fields,
  form,
  getOrigin,
  save,
  onSaved,
  editSection: controlledEditSection,
  onEditSectionChange,
}: {
  fields: Record<K, (keyof StudentProfileForm)[]>;
  form: SectionForm;
  /** 취소 시 되돌릴 원본 값. */
  getOrigin: () => Partial<StudentProfileForm> | undefined;
  /** 변경분 계산 + API 호출까지 화면이 담당한다. 변경이 없었으면 false. */
  save: (section: K) => Promise<boolean>;
  /** 저장 성공 후 재조회. */
  onSaved: () => void;
  /**
   * 편집 대상 상태를 상위에서 함께 관리해야 할 때만 넘긴다.
   * (자체저장 카드와 편집 대상을 공유해 한 번에 하나만 열리게 하는 화면 등)
   * 생략하면 훅이 내부 state로 관리한다.
   */
  editSection?: K | null;
  onEditSectionChange?: (section: K | null) => void;
}) {
  const dispatch = useAppDispatch();
  const { getValues, setValue, trigger, watch, reset } = form;

  const [internalEditSection, setInternalEditSection] = useState<K | null>(
    null,
  );
  const editSection =
    controlledEditSection !== undefined
      ? controlledEditSection
      : internalEditSection;
  const setEditSection = onEditSectionChange ?? setInternalEditSection;

  const [isSaving, setIsSaving] = useState(false);
  /** 저장 중복 요청 방지. 화면 갱신을 기다리지 않고 즉시 막아야 하므로 state와 별도로 둔다. */
  const savingRef = useRef(false);
  /** 저장하기에서 검증에 걸린 영역. 이 영역은 값을 고치는 즉시 재검증한다. */
  const [revalidateSection, setRevalidateSection] = useState<K | null>(null);

  /**
   * 저장하기에서 검증에 걸린 뒤에는 값을 고치는 즉시 오류 표시가 사라지도록 한다.
   * trigger로 직접 검증하면 formState.isSubmitted가 켜지지 않아
   * react-hook-form의 reValidateMode(기본 onChange)가 동작하지 않기 때문에 직접 구독한다.
   */
  useEffect(() => {
    if (!revalidateSection) return;

    const isSectionField = (name: string) =>
      fields[revalidateSection].some(
        // 보호자처럼 배열인 필드는 하위 경로(parents.0.name)까지 포함한다.
        (field) => name === field || name.startsWith(`${field}.`),
      );

    const subscription = watch((_, { name }) => {
      // 보호자 추가/삭제처럼 특정 필드의 변경이 아닌 경우는 건너뛴다.
      if (!name || !isSectionField(name)) return;
      trigger(name as Path<StudentProfileForm>);
    });

    return () => subscription.unsubscribe();
  }, [revalidateSection, watch, trigger, fields]);

  /**
   * 저장 전 해당 영역 입력값의 앞뒤 공백을 제거한다.
   * 화면 값 자체를 바꾸므로 이후의 검증/변경 비교도 공백이 제거된 값으로 수행된다.
   */
  const trimSectionValues = (section: K) => {
    const trim = (path: Path<StudentProfileForm>) => {
      const value = getValues(path);
      if (typeof value !== 'string' || value === value.trim()) return;
      setValue(path, value.trim() as never);
    };

    fields[section].forEach((field) => {
      if (field !== 'parents') {
        trim(field as Path<StudentProfileForm>);
        return;
      }
      // 보호자는 배열이므로 항목별 문자열 입력을 하나씩 훑는다.
      getValues('parents')?.forEach((parent, index) =>
        Object.keys(parent).forEach((key) =>
          trim(`parents.${index}.${key}` as Path<StudentProfileForm>),
        ),
      );
    });
  };

  const startEdit = (section: K) => {
    setEditSection(section);
    setRevalidateSection(null);
  };

  /** 수정 중인 영역의 값만 조회 시점으로 되돌린다. (다른 영역 입력은 건드리지 않는다) */
  const cancel = (section: K) => {
    const origin = getOrigin();
    const restored = fields[section].reduce(
      (acc, field) => ({
        ...acc,
        // parents는 useFieldArray가 배열이 아닌 값을 무시하므로 빈 배열로 맞춘다.
        [field]: field === 'parents' ? (origin?.parents ?? []) : origin?.[field],
      }),
      {},
    );
    reset({ ...getValues(), ...restored });
    setEditSection(null);
    setRevalidateSection(null);
  };

  /** 수정 중인 영역만 저장한다. 검증도 그 영역의 필드만 수행한다. */
  const submit = async (section: K) => {
    // 보호자는 배열이 통째로 교체되므로 중복 요청이 특히 위험하다.
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);

    try {
      trimSectionValues(section);

      // 유효성 검사는 각 입력의 rules에서 처리한다. (보호자 항목은 parentRules)
      // shouldFocus: 오류 항목이 스크롤 밖에 있어도 사용자가 알 수 있도록 첫 오류로 이동한다.
      const isValid = await trigger(
        fields[section] as Path<StudentProfileForm>[],
        { shouldFocus: true },
      );
      // 한 번 검증에 걸린 뒤부터는 입력을 고치는 즉시 오류 표시가 갱신된다.
      setRevalidateSection(isValid ? null : section);
      if (!isValid) return;

      try {
        const changed = await save(section);
        if (!changed) {
          dispatch(openAlert({ message: '변경된 내용이 없습니다.' }));
          setEditSection(null);
          return;
        }

        onSaved();
        dispatch(openAlert({ message: '수정이 완료되었습니다.' }));
        setEditSection(null);
      } catch (err) {
        console.error(err);
        // 네트워크/500 에러는 인터셉터가 이미 알럿을 띄운다. (__handled)
        if ((err as { __handled?: boolean })?.__handled) return;
        const message = (err as { response?: { data?: { message?: string } } })
          ?.response?.data?.message;
        dispatch(openAlert({ message: message ?? '수정에 실패했습니다.' }));
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return { editSection, isSaving, startEdit, cancel, submit };
}
